/**
 * App.tsx — React Router 路由配置
 *
 * All protected routes are wrapped with LayoutConfigProvider
 * so every child component can access DSL config via useLayoutConfigContext().
 */

import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { isBuilderAccountUser } from '@/lib/builder-access';
import { isChatOnlyAccess, isChatOnlyPath } from '@/lib/chat-only-access';
import { useLayoutConfigContext } from '@/lib/use-layout-config';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { LayoutConfigProvider } from '@/components/LayoutConfigProvider';
import { PagePerformanceTracer } from '@/components/PagePerformanceTracer';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/sonner';
import { AppLayout } from '@/layouts/AppLayout';
import {
  dynamicRuntimeBindingForPath,
  runtimeRouteForDynamicRuntimePath,
} from '@/lib/dynamic-runtime-bindings';
import { shouldUseDynamicRuntimePage } from '@/lib/runtime-api';
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const AdminLoginPage = lazy(() => import('@/pages/AdminLoginPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const TwoFactorPage = lazy(() => import('@/pages/TwoFactorPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const DailySchedulePage = lazy(() => import('@/pages/oms/DailySchedulePage'));
const WorkSurfacePage = lazy(() => import('@/pages/WorkSurfacePage'));
const EntityListPage = lazy(() => import('@/pages/EntityListPage'));
const EntityDetailPage = lazy(() => import('@/pages/EntityDetailPage'));
const CustomAppViewPage = lazy(() => import('@/pages/admin/CustomAppViewPage'));
const CategoryWorkspacePage = lazy(() => import('@/pages/CategoryWorkspacePage'));
const ShareViewPage = lazy(() => import('@/pages/ShareViewPage'));
const SharedChatPage = lazy(() => import('@/pages/SharedChatPage'));
const DemoLandingPage = lazy(() => import('@/pages/demo/DemoLandingPage'));
const SdkDataExtensionPage = lazy(() => import('@/pages/sdk/SdkDataExtensionPage'));
const SdkOnlineModuleRuntimePage = lazy(() => import('@/pages/sdk/SdkOnlineModuleRuntimePage'));
const ScannerPage = lazy(() => import('@/pages/ScannerPage'));
const AdminRoutes = lazy(() => import('@/routes/AdminRoutes'));
const AccountingRoutes = lazy(() => import('@/routes/AccountingRoutes'));
const FinanceRoutes = lazy(() => import('@/routes/FinanceRoutes'));
const DepotRoutes = lazy(() => import('@/routes/DepotRoutes'));
const SocRoutes = lazy(() => import('@/routes/SocRoutes'));
const Pro2Routes = lazy(() => import('@/routes/Pro2Routes'));
const DriverRoutes = lazy(() => import('@/routes/DriverRoutes'));
const AuditRoutes = lazy(() => import('@/routes/AuditRoutes'));
const BrainRoutes = lazy(() => import('@/routes/BrainRoutes'));
const SupportRoutes = lazy(() => import('@/routes/SupportRoutes'));
const OmsRoutes = lazy(() => import('@/routes/OmsRoutes'));
const CrmRoutes = lazy(() => import('@/routes/CrmRoutes'));
const ScmRoutes = lazy(() => import('@/routes/ScmRoutes'));
const OperationalIntelligenceRoutes = lazy(() => import('@/routes/OperationalIntelligenceRoutes'));
const SvcRoutes = lazy(() => import('@/routes/SvcRoutes'));
const FlmRoutes = lazy(() => import('@/routes/FlmRoutes'));
const NetworkRoutes = lazy(() => import('@/routes/NetworkRoutes'));
const WmRoutes = lazy(() => import('@/routes/WmRoutes'));
const WpRoutes = lazy(() => import('@/routes/WpRoutes'));
const LogisticsNetworkCapabilityPage = lazy(() => import('@/pages/network/LogisticsNetworkCapabilityPage'));
const BusinessBuilderHomePage = lazy(() => import('@/pages/builder/BusinessBuilderHomePage'));
const BusinessBuilderArtifactsPage = lazy(() => import('@/pages/builder/BusinessBuilderArtifactsPage'));
const BusinessBuilderTreePage = lazy(() => import('@/pages/builder/BusinessBuilderTreePage'));
const BusinessBuilderMapPage = lazy(() => import('@/pages/builder/BusinessBuilderMapPage'));
const BusinessBuilderAiCosWorkbenchPage = lazy(() => import('@/pages/builder/BusinessBuilderAiCosWorkbenchPage'));
const DynamicModuleRuntimePage = lazy(() => import('@/pages/dynamic-runtime/DynamicModuleRuntimePage'));
const ReportsHubPage = lazy(() => import('@/pages/ReportsHubPage'));
const ChatPage = lazy(() => import('@/pages/ChatPage'));
const AiWorkforcePage = lazy(() => import('@/pages/aiw/AiWorkforcePage'));
const ScmQuotationsPage = lazy(() => import('@/pages/scm/ScmQuotationsPage'));
const ScmSalesOrdersPage = lazy(() => import('@/pages/scm/ScmSalesOrdersPage'));
const ScmPurchaseOrdersPage = lazy(() => import('@/pages/scm/ScmPurchaseOrdersPage'));
const ScmRfqPage = lazy(() => import('@/pages/scm/ScmRfqPage'));
const ConversationsInboxPage = lazy(() => import('@/pages/inbox/ConversationsInboxPage'));
const EmailMessagesInboxPage = lazy(() => import('@/pages/inbox/EmailMessagesInboxPage'));
const MessagingPage = lazy(() => import('@/pages/inbox/MessagingPage'));
const IntakeReviewPage = lazy(() => import('@/pages/intake/IntakeReviewPage'));
const WorkItemsReviewPage = lazy(() => import('@/pages/inbox/WorkItemsReviewPage'));
const TmsOutboxPage = lazy(() => import('@/pages/tms/TmsOutboxPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const SettingsGeneralPage = lazy(() => import('@/pages/settings/SettingsGeneralPage'));
const SettingsTeamPage = lazy(() => import('@/pages/settings/SettingsTeamPage'));
const SettingsBillingPage = lazy(() => import('@/pages/settings/SettingsBillingPage'));
const SettingsProfilePage = lazy(() => import('@/pages/settings/SettingsProfilePage'));
const SettingsCompanyPage = lazy(() => import('@/pages/settings/SettingsCompanyPage'));
const SettingsStorefrontPage = lazy(() => import('@/pages/settings/SettingsStorefrontPage'));
const SettingsSurfaceAccessPage = lazy(() => import('@/pages/settings/SettingsSurfaceAccessPage'));
const SettingsOperationsPage = lazy(() => import('@/pages/settings/SettingsOperationsPage'));
const KnowledgeBasePage = lazy(() => import('@/pages/settings/KnowledgeBasePage'));
const DocumentTemplatePage = lazy(() => import('@/pages/admin/DocumentTemplatePage'));
const OperationalDashboard = lazy(() => import('@/pages/dashboards/OperationalDashboard'));
const ManagementDashboard = lazy(() => import('@/pages/dashboards/ManagementDashboard'));
const ExecutiveIntelligencePage = lazy(() => import('@/pages/dashboards/ExecutiveIntelligencePage'));
const GoalCommandCenterPage = lazy(() => import('@/pages/brain/GoalCommandCenterPage')); // Gen7

function isNewRevoTenant(tenantSlug?: string | null): boolean {
  return String(tenantSlug ?? '').trim().toLowerCase() === 'new-revo';
}

function ConfiguredLandingPage() {
  const { config } = useLayoutConfigContext();
  const { user } = useAuth();
  const location = useLocation();
  const landingRoute = config.landingRoute;

  // platform_admin always lands on CC Dashboard, not the tenant dashboard
  if (user?.role === 'platform_admin') {
    return <Navigate to="/admin/platform-dynamic" replace />;
  }

  if (landingRoute && landingRoute !== location.pathname) {
    return <Navigate to={landingRoute} replace />;
  }

  return <DashboardPage />;
}

function RedirectPlatformAdminToConsole({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (user?.role === 'platform_admin') {
    return <Navigate to="/admin/platform-dynamic" replace />;
  }
  return <>{children}</>;
}

function RouteLoadingFallback() {
  return (
    <div className="flex min-h-[240px] items-center justify-center">
      <div className="h-7 w-7 animate-spin rounded-full border-2 border-muted border-t-primary" />
    </div>
  );
}

function LazyRoute({ children }: { children: ReactNode }) {
  return <Suspense fallback={<RouteLoadingFallback />}>{children}</Suspense>;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireAppAccess({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();

  if (isChatOnlyAccess(user) && !isChatOnlyPath(location.pathname)) {
    return <Navigate to="/chat" replace />;
  }

  return <>{children}</>;
}

function RequireAiChatAccess({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  if (!user) return <Navigate to="/login" replace />;
  if (isNewRevoTenant(user.tenantSlug)) return <Navigate to="/wm" replace />;
  if (isChatOnlyAccess(user)) return <>{children}</>;
  const enabledModules = new Set((user.enabledModules ?? []).map((moduleId) => moduleId.toUpperCase()));
  if (!enabledModules.has('AI') && !enabledModules.has('AIC') && !enabledModules.has('AIW')) return <Navigate to="/" replace />;

  return <>{children}</>;
}

function RequireOperationalIntelligenceAccess({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  if (!user) return <Navigate to="/login" replace />;
  if (!(user.enabledModules ?? []).includes('OPI')) return <Navigate to="/" replace />;

  return <>{children}</>;
}

function RequireBuilderAccountAccess({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  if (!user) return <Navigate to="/login" replace />;
  if (!isBuilderAccountUser(user)) return <Navigate to="/" replace />;

  return <>{children}</>;
}

function LegacyInboundEntryRedirect() {
  const location = useLocation();
  return <Navigate to={`/wm/inbound/new${location.search || ''}`} replace />;
}

function LegacyTmsSdkRedirect() {
  const location = useLocation();
  const targetPath = location.pathname.replace(/^\/tms\b/, '/tm');
  return <Navigate to={`${targetPath}${location.search || ''}`} replace />;
}

function LegacyWmsSdkRedirect() {
  const location = useLocation();
  const { user } = useAuth();
  if (user && !Array.isArray(user.enabledModules)) {
    return <div className="p-6 text-sm text-muted-foreground">Loading module access...</div>;
  }
  const modules = new Set((user?.enabledModules ?? []).map((moduleId) => moduleId.toUpperCase()));
  const targetBase = modules.has('WP') && !modules.has('WM') && !modules.has('WMS')
    ? '/wp'
    : modules.has('WG') && !modules.has('WM') && !modules.has('WMS')
      ? '/wg'
      : '/wm';
  const targetPath = location.pathname.replace(/^\/wms\b/, targetBase);
  return <Navigate to={`${targetPath}${location.search || ''}`} replace />;
}

function WmOrWpRoutes() {
  const location = useLocation();
  const { user } = useAuth();
  if (user && !Array.isArray(user.enabledModules)) {
    return <div className="p-6 text-sm text-muted-foreground">Loading module access...</div>;
  }
  const modules = new Set((user?.enabledModules ?? []).map((moduleId) => moduleId.toUpperCase()));

  if (modules.has('WP') && !modules.has('WM') && !modules.has('WMS')) {
    const targetPath = location.pathname.replace(/^\/wm\b/, '/wp');
    return <Navigate to={`${targetPath}${location.search || ''}`} replace />;
  }

  if (modules.has('WG') && !modules.has('WM') && !modules.has('WMS')) {
    const targetPath = location.pathname.replace(/^\/wm\b/, '/wg');
    return <Navigate to={`${targetPath}${location.search || ''}`} replace />;
  }

  return <LazyRoute><WmRoutes /></LazyRoute>;
}

function LegacyHmsSdkRedirect() {
  const location = useLocation();
  const targetPath = location.pathname.replace(/^\/hms\b/, '/ct');
  return <Navigate to={`${targetPath}${location.search || ''}`} replace />;
}

function LegacyFmsSdkRedirect() {
  const location = useLocation();
  const parts = location.pathname.split('/').filter(Boolean);
  const page = parts[1] ?? '';
  const rest = parts.slice(2).join('/');
  const mappedPath = (() => {
    if (location.pathname === '/fms') return '/fm';
    if (page === 'ocean-import') return '/fm/import';
    if (page === 'ocean-export') return '/fm/export';
    if (page === 'transhipment') return '/fm/transhipment';
    if (page === 'shipments') return rest ? `/fm/shipments/${rest}` : '/fm/shipments';
    if (page === 'documents') return '/fm/documents';
    if (page === 'rates') return '/fm/tariffs';
    if (page === 'setup') return '/fm/master-data';
    if (page === 'port-system') return '/fm/integrations/portnet';
    if (page === 'consol') return '/fm/consols';
    if (page === 'air') return '/fm/air';
    if (page === 'cross-trade') return '/fm/cross-trade';
    if (page === 'local-handling') return '/fm/local-handling';
    if (page === 'billing' || page === 'costing' || page === 'accounting') return '/fm/billing';
    if (page === 'reports') return '/fm/reports';
    if (page === 'containers' || page === 'cargo') return '/fm/shipments';
    if (page === 'schedule') return '/fm/schedule';
    return '/fm';
  })();
  return <Navigate to={`${mappedPath}${location.search || ''}`} replace />;
}

function LegacyScmRouteSwitch() {
  const location = useLocation();
  if (/^\/scm(?:\/(?:quotations|rfq|sales-orders|purchase-orders))?(?:\/.*)?$/i.test(location.pathname)) {
    return <LazyRoute><ScmRoutes /></LazyRoute>;
  }
  const binding = dynamicRuntimeBindingForPath(location.pathname);
  const targetPath = binding?.module === 'sc'
    ? runtimeRouteForDynamicRuntimePath(location.pathname, binding)
    : location.pathname.replace(/^\/scm\b/, '/sc');
  return <Navigate to={`${targetPath}${location.search || ''}${location.hash || ''}`} replace />;
}

function LegacyPrefixSdkRedirect({ from, to }: { from: RegExp; to: string }) {
  const location = useLocation();
  const targetPath = location.pathname.replace(from, to);
  return <Navigate to={`${targetPath}${location.search || ''}${location.hash || ''}`} replace />;
}

function RuntimeModuleRouteSwitch() {
  const location = useLocation();
  return shouldUseDynamicRuntimePage(location.pathname)
    ? <DynamicModuleRuntimePage />
    : <SdkOnlineModuleRuntimePage />;
}

export default function App() {
  return (
    <TooltipProvider>
      <ErrorBoundary>
      <BrowserRouter>
        <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          {/* Public — LoginPage reads branding from its own config fetch */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-2fa" element={<TwoFactorPage />} />
          <Route path="/inbound/new" element={<LegacyInboundEntryRedirect />} />
          <Route path="/share/:moduleId/:token" element={<ShareViewPage />} />
          <Route path="/share/:token" element={<ShareViewPage />} />
          <Route path="/share/chat/:token" element={<SharedChatPage />} />
          <Route path="/demo" element={<DemoLandingPage />} />
          <Route path="/demo/logistics-capability" element={<LazyRoute><LogisticsNetworkCapabilityPage /></LazyRoute>} />
          <Route path="/demo/business-builder-map" element={<Navigate to="/builder/world-view" replace />} />
          <Route path="/demo/sdk-data-extension" element={<Navigate to="/admin/sdk-data" replace />} />

          {/* Protected — LayoutConfigProvider supplies DSL config to all children */}
          <Route
            element={
              <RequireAuth>
                <RequireAppAccess>
                  <LayoutConfigProvider>
                    <PagePerformanceTracer />
                    <AppLayout />
                  </LayoutConfigProvider>
                </RequireAppAccess>
              </RequireAuth>
            }
          >
            <Route index element={<ConfiguredLandingPage />} />
            <Route path="today" element={<DailySchedulePage />} />
            <Route path="om" element={<LazyRoute><WorkSurfacePage /></LazyRoute>} />
            <Route path="work-surface" element={<LazyRoute><WorkSurfacePage /></LazyRoute>} />
            <Route path="builder" element={<Navigate to="/builder/business-home" replace />} />
            <Route path="builder/business-home" element={<LazyRoute><RequireBuilderAccountAccess><BusinessBuilderHomePage /></RequireBuilderAccountAccess></LazyRoute>} />
            <Route path="builder/organization" element={<LazyRoute><RequireBuilderAccountAccess><BusinessBuilderTreePage /></RequireBuilderAccountAccess></LazyRoute>} />
            <Route path="builder/business-tree" element={<Navigate to="/builder/organization" replace />} />
            <Route path="builder/artifacts" element={<LazyRoute><RequireBuilderAccountAccess><BusinessBuilderArtifactsPage /></RequireBuilderAccountAccess></LazyRoute>} />
            <Route path="builder/world-view" element={<LazyRoute><RequireBuilderAccountAccess><BusinessBuilderMapPage /></RequireBuilderAccountAccess></LazyRoute>} />
            <Route path="builder/ai-cos-workbench" element={<LazyRoute><RequireBuilderAccountAccess><BusinessBuilderAiCosWorkbenchPage /></RequireBuilderAccountAccess></LazyRoute>} />
            <Route path="builder/review-queue" element={<LazyRoute><RequireBuilderAccountAccess><BusinessBuilderMapPage /></RequireBuilderAccountAccess></LazyRoute>} />
            <Route path="builder/business-map" element={<Navigate to="/builder/world-view" replace />} />
            <Route path="ai/inbox" element={<LazyRoute><AiWorkforcePage /></LazyRoute>} />
            <Route path="aiw" element={<LazyRoute><AiWorkforcePage /></LazyRoute>} />
            <Route path="aiw/inbox" element={<LazyRoute><AiWorkforcePage /></LazyRoute>} />
            <Route path="aiw/*" element={<LegacyPrefixSdkRedirect from={/^\/aiw\b/} to="/ai" />} />
            <Route path="aic/*" element={<LegacyPrefixSdkRedirect from={/^\/aic\b/} to="/ai" />} />
            {/* Gen7: Goal Command Center — must be before /e/:slug */}
            <Route path="e/goals" element={<LazyRoute><GoalCommandCenterPage /></LazyRoute>} />
            <Route path="e/:slug" element={<EntityListPage />} />
            <Route path="e/:slug/:id" element={<EntityDetailPage />} />
            <Route path="chat" element={<RequireAiChatAccess><LazyRoute><ChatPage /></LazyRoute></RequireAiChatAccess>} />
            <Route path="chat/:convId" element={<RequireAiChatAccess><LazyRoute><ChatPage /></LazyRoute></RequireAiChatAccess>} />
            <Route path="inbox/conversations" element={<LazyRoute><ConversationsInboxPage /></LazyRoute>} />
            <Route path="inbox/email-messages" element={<LazyRoute><EmailMessagesInboxPage /></LazyRoute>} />
            <Route path="inbox/messaging" element={<LazyRoute><MessagingPage /></LazyRoute>} />
            <Route path="inbox/outbox" element={<LazyRoute><TmsOutboxPage /></LazyRoute>} />
            <Route path="inbox/intake-review" element={<LazyRoute><IntakeReviewPage /></LazyRoute>} />
            <Route path="inbox/work-items" element={<LazyRoute><WorkItemsReviewPage /></LazyRoute>} />

            <Route path="admin/*" element={<LazyRoute><AdminRoutes /></LazyRoute>} />
            <Route path="app/:slug" element={<CustomAppViewPage />} />

            {/* ── Document Scanner — back-office snap + AI OCR ── */}
            <Route path="scanner" element={<ScannerPage />} />

            {/* ── Per-category workspace/overview dashboards ── */}
            <Route path="workspace" element={<ConfiguredLandingPage />} />
            <Route path="workspace/:category" element={<RedirectPlatformAdminToConsole><CategoryWorkspacePage /></RedirectPlatformAdminToConsole>} />
            <Route path="sdk/data-extension" element={<LazyRoute><SdkDataExtensionPage /></LazyRoute>} />
            {/* ── Cross-module Dashboards ── */}
            <Route path="ops" element={<LazyRoute><OperationalDashboard /></LazyRoute>} />
            <Route path="management" element={<LazyRoute><ManagementDashboard /></LazyRoute>} />

            {/* Settings */}
            <Route path="settings" element={<LazyRoute><SettingsPage /></LazyRoute>}>
              <Route index element={<Navigate to="company" replace />} />
              <Route path="general" element={<LazyRoute><SettingsGeneralPage /></LazyRoute>} />
              <Route path="profile" element={<LazyRoute><SettingsProfilePage /></LazyRoute>} />
              <Route path="company" element={<LazyRoute><SettingsCompanyPage /></LazyRoute>} />
              <Route path="storefront" element={<LazyRoute><SettingsStorefrontPage /></LazyRoute>} />
              <Route path="surface-access" element={<LazyRoute><SettingsSurfaceAccessPage /></LazyRoute>} />
              <Route path="running-numbers" element={<LazyRoute><SettingsOperationsPage /></LazyRoute>} />
              <Route path="recycle-bin" element={<LazyRoute><SettingsOperationsPage /></LazyRoute>} />
              <Route path="activity-log" element={<LazyRoute><SettingsOperationsPage /></LazyRoute>} />
              <Route path="event-log" element={<LazyRoute><SettingsOperationsPage /></LazyRoute>} />
              <Route path="team" element={<LazyRoute><SettingsTeamPage /></LazyRoute>} />
              <Route path="billing" element={<LazyRoute><SettingsBillingPage /></LazyRoute>} />
              <Route path="templates" element={<LazyRoute><DocumentTemplatePage /></LazyRoute>} />
              <Route path="knowledge-base" element={<LazyRoute><KnowledgeBasePage /></LazyRoute>} />
            </Route>

            {/* Legacy transport browser mounts stay in front of dynamic runtime mounts. */}
            <Route path="tms/*" element={<LegacyTmsSdkRedirect />} />
            <Route path="wms/*" element={<LegacyWmsSdkRedirect />} />
            <Route path="hms/*" element={<LegacyHmsSdkRedirect />} />
            <Route path="fms/*" element={<LegacyFmsSdkRedirect />} />
            <Route path="acc/*" element={<LegacyPrefixSdkRedirect from={/^\/acc\b/} to="/ac" />} />
            <Route path="ac/*" element={<LazyRoute><AccountingRoutes /></LazyRoute>} />
            <Route path="wp/*" element={<LazyRoute><WpRoutes /></LazyRoute>} />
            <Route path="wg/*" element={<LazyRoute><DynamicModuleRuntimePage /></LazyRoute>} />
            <Route path="wm/*" element={<WmOrWpRoutes />} />
            <Route path="scm/*" element={<LegacyScmRouteSwitch />} />
            <Route path="rm/portal-preview" element={<LazyRoute><DynamicModuleRuntimePage /></LazyRoute>} />
            <Route path="rm/share-preview" element={<LazyRoute><DynamicModuleRuntimePage /></LazyRoute>} />
            <Route path="rm/*" element={<LazyRoute><CrmRoutes /></LazyRoute>} />
            <Route path="crm/*" element={<LegacyPrefixSdkRedirect from={/^\/crm\b/} to="/rm" />} />
            <Route path="maritime-desk/*" element={<LegacyPrefixSdkRedirect from={/^\/maritime-desk\b/} to="/mt" />} />
            <Route path="shipping-agency/*" element={<LegacyPrefixSdkRedirect from={/^\/shipping-agency\b/} to="/mt" />} />
            <Route path="liner/*" element={<LegacyPrefixSdkRedirect from={/^\/liner\b/} to="/mt" />} />

            {/* ── OMS — Business / RM Core ── */}
            <Route path="oms/*" element={<LazyRoute><OmsRoutes /></LazyRoute>} />
            <Route path="customer-support/*" element={<LazyRoute><SupportRoutes /></LazyRoute>} />

            <Route path="svc/*" element={<LazyRoute><SvcRoutes /></LazyRoute>} />
            <Route path="flm/*" element={<LazyRoute><FlmRoutes /></LazyRoute>} />

            <Route path="driver/*" element={<LazyRoute><DriverRoutes /></LazyRoute>} />


            <Route path="finance/*" element={<LazyRoute><FinanceRoutes /></LazyRoute>} />

            <Route path="operational-intelligence/*" element={<LazyRoute><RequireOperationalIntelligenceAccess><OperationalIntelligenceRoutes /></RequireOperationalIntelligenceAccess></LazyRoute>} />

            <Route path="executive-intelligence" element={<LazyRoute><ExecutiveIntelligencePage /></LazyRoute>} />

            <Route path="audit/*" element={<LazyRoute><AuditRoutes /></LazyRoute>} />
            <Route path="brain/*" element={<LazyRoute><BrainRoutes /></LazyRoute>} />

            <Route path="depot/*" element={<LazyRoute><DepotRoutes /></LazyRoute>} />
            <Route path="soc/*" element={<LazyRoute><SocRoutes /></LazyRoute>} />
            <Route path="pro2/*" element={<LazyRoute><Pro2Routes /></LazyRoute>} />

            <Route path="network/logistics-capability" element={<LazyRoute><LogisticsNetworkCapabilityPage /></LazyRoute>} />
            <Route path="network/*" element={<LazyRoute><NetworkRoutes /></LazyRoute>} />

            {/* ── Cross-module Reports Hub ── */}
            <Route path="reports" element={<LazyRoute><ReportsHubPage /></LazyRoute>} />

            {/* SC runtime commercial routes stay on SDK pages until dynamic runtime reaches parity. */}
            <Route path="sc/quotations" element={<LazyRoute><ScmQuotationsPage /></LazyRoute>} />
            <Route path="sc/rfq" element={<LazyRoute><ScmRfqPage /></LazyRoute>} />
            <Route path="sc/sales-orders" element={<LazyRoute><ScmSalesOrdersPage /></LazyRoute>} />
            <Route path="sc/purchase-orders" element={<LazyRoute><ScmPurchaseOrdersPage /></LazyRoute>} />

            {/* Runtime modules resolved from registry or generic module namespace. Must stay near the end. */}
            <Route path="pwa/:moduleId/*" element={<LazyRoute><RuntimeModuleRouteSwitch /></LazyRoute>} />
            <Route path="portal/:moduleId/*" element={<LazyRoute><RuntimeModuleRouteSwitch /></LazyRoute>} />
            <Route path=":moduleId/*" element={<LazyRoute><RuntimeModuleRouteSwitch /></LazyRoute>} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        <Toaster />
      </BrowserRouter>
      </ErrorBoundary>
    </TooltipProvider>
  );
}
